import { useState, useEffect, useMemo } from 'react';
import { CheckCircle2, AlertCircle, X, Sparkles, ChevronDown, ChevronRight, Mail } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { PriceBookMatchDialog } from './PriceBookMatchDialog';
import { ComplianceLineItemDetail } from './ComplianceLineItemDetail';
import type { NonComplianceMessage } from './VendorNotificationPopout';
import type { ComplianceCheck } from '@/services';
import type { ComplianceLineItem } from '@/services';

interface InvoiceItem {
  id: string;
  sequence: number;
  description: string;
  item_code: string | null;
  quantity: number | null;
  uom: string | null;
  unit_price: number | null;
  line_total: number | null;
  price_book_item_id: string | null;
  discount: number | null;
}

interface TicketItem {
  id: string;
  description: string;
  item_code: string | null;
  quantity: number | null;
  uom: string | null;
  unit_price: number | null;
  discount: number | null;
  line_total: number | null;
  price_book_item_id: string | null;
}

interface PriceBookItem {
  id: string;
  description: string;
  itemcode: string;
  price: number;
  discount: number;
  unit: string | null;
  uom_id: string | null;
}

type NonComplianceFilter = 'qtyMismatch' | 'priceMismatch' | 'unsupportedItem' | 'noTicketSupport' | null;

interface InvoiceItemsComparisonTableProps {
  invoiceItems: InvoiceItem[];
  ticketItems: TicketItem[];
  priceBookItems: PriceBookItem[];
  hasPriceBook: boolean;
  hasTicket: boolean;
  complianceFilter?: NonComplianceFilter;
  onComplianceFilterClear?: () => void;
  onSelectionChange?: (selectedPriceBookItemIds: string[]) => void;
  onNotificationRequest?: (messages: NonComplianceMessage[]) => void;
  complianceChecks?: ComplianceCheck[];
  complianceLineItems?: ComplianceLineItem[];
  showComplianceBadges?: boolean;
}

interface ComparisonGroup {
  invoiceItem: InvoiceItem;
  ticketItem: TicketItem | null;
  priceBookItem: PriceBookItem | null;
  hasMismatch: boolean;
  qtyMismatch: boolean;
  priceMismatch: boolean;
  priceMismatchOnPriceBook: boolean;
  priceMismatchOnTicket: boolean;
  totalMismatch: boolean;
  missingPriceBookItem: boolean;
  missingTicketItem: boolean;
}

const complianceFilterLabels: Record<string, string> = {
  qtyMismatch: 'Qty Mismatches',
  priceMismatch: 'Price Mismatches',
  unsupportedItem: 'Non-contract Items',
  noTicketSupport: 'No Ticket Support',
};

export function InvoiceItemsComparisonTable({
  invoiceItems,
  ticketItems,
  priceBookItems,
  hasPriceBook,
  hasTicket,
  complianceFilter,
  onComplianceFilterClear,
  onSelectionChange,
  onNotificationRequest,
  complianceChecks,
  complianceLineItems,
  showComplianceBadges = false,
}: InvoiceItemsComparisonTableProps) {
  const [showOnlyInvoice, setShowOnlyInvoice] = useState(false);
  const [showOnlyNonCompliant, setShowOnlyNonCompliant] = useState(false);
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
  const [matchDialogItem, setMatchDialogItem] = useState<InvoiceItem | null>(null);

  const [expandedAiRows, setExpandedAiRows] = useState<Set<string>>(new Set());

  // Build lookup maps for AI compliance line items
  const complianceByItemId = useMemo(() => {
    const map = new Map<string, ComplianceLineItem>();
    if (complianceLineItems) {
      for (const item of complianceLineItems) {
        if (item.invoice_item_id) map.set(item.invoice_item_id, item);
      }
    }
    return map;
  }, [complianceLineItems]);

  // Fallback maps for older runs that lack invoice_item_id
  const complianceByCode = useMemo(() => {
    const map = new Map<string, ComplianceLineItem>();
    if (complianceLineItems) {
      for (const item of complianceLineItems) {
        if (item.item_code) map.set(item.item_code.toLowerCase(), item);
      }
    }
    return map;
  }, [complianceLineItems]);

  const complianceByLineNumber = useMemo(() => {
    const map = new Map<number, ComplianceLineItem>();
    if (complianceLineItems) {
      for (const item of complianceLineItems) {
        map.set(item.line_number, item);
      }
    }
    return map;
  }, [complianceLineItems]);

  const toggleAiRow = (id: string) => {
    setExpandedAiRows((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // Build a lookup map: invoice_item_id -> ComplianceCheck
  const checksByItemId = useMemo(() => {
    const map: Record<string, ComplianceCheck> = {};
    if (complianceChecks) {
      for (const c of complianceChecks) {
        map[c.invoice_item_id] = c;
      }
    }
    return map;
  }, [complianceChecks]);

  const hasPersistedChecks = complianceChecks && complianceChecks.length > 0;

  // When a compliance filter is applied from the cards, reset the checkbox filters
  useEffect(() => {
    if (complianceFilter) {
      setShowOnlyNonCompliant(false);
      setShowOnlyInvoice(false);
    }
  }, [complianceFilter]);

  // Emit selected price book item IDs to parent
  useEffect(() => {
    if (!onSelectionChange) return;
    const pbItemIds = invoiceItems
      .filter(item => selectedItems.has(item.id) && item.price_book_item_id != null)
      .map(item => item.price_book_item_id!);
    onSelectionChange([...new Set(pbItemIds)]);
  }, [selectedItems, invoiceItems, onSelectionChange]);

  // Build comparison groups by matching on price_book_item_id or item_code
  // Still needed for side-by-side display. Flags come from persisted checks when available.
  const comparisonGroups: ComparisonGroup[] = invoiceItems.map((invItem) => {
    // Match ticket item
    const ticketItem = ticketItems.find(
      (ti) =>
        (invItem.price_book_item_id && ti.price_book_item_id === invItem.price_book_item_id) ||
        (invItem.item_code && ti.item_code === invItem.item_code)
    ) || null;

    // Match price book item
    const priceBookItem = priceBookItems.find(
      (pbi) =>
        pbi.id === invItem.price_book_item_id ||
        (invItem.item_code && pbi.itemcode === invItem.item_code)
    ) || null;

    // Check compliance engine result for FAIL/FLAG
    const aiMatch = complianceByItemId.get(invItem.id)
      ?? (invItem.item_code ? complianceByCode.get(invItem.item_code.toLowerCase()) : undefined)
      ?? complianceByLineNumber.get(invItem.sequence)
      ?? null;
    const aiNonCompliant = aiMatch?.status === 'FAIL' || aiMatch?.status === 'FLAG';

    // Use persisted compliance check flags if available, otherwise fall back to inline computation
    const check = checksByItemId[invItem.id];
    if (check && hasPersistedChecks) {
      return {
        invoiceItem: invItem,
        ticketItem,
        priceBookItem,
        hasMismatch: check.has_mismatch || aiNonCompliant,
        qtyMismatch: check.qty_mismatch_on_ticket_item,
        priceMismatch: check.price_mismatch_on_price_book_item || check.price_mismatch_on_ticket_item,
        priceMismatchOnPriceBook: check.price_mismatch_on_price_book_item,
        priceMismatchOnTicket: check.price_mismatch_on_ticket_item,
        totalMismatch: check.total_mismatch_on_ticket_item,
        missingPriceBookItem: check.missing_price_book_item,
        missingTicketItem: check.missing_ticket_item,
      };
    }

    // Fallback: inline computation (when no persisted checks exist)
    const qtyMismatch = ticketItem !== null && invItem.quantity !== ticketItem.quantity;
    const invDiscountedPrice = invItem.unit_price !== null ? invItem.unit_price * (1 - (invItem.discount ?? 0) / 100) : null;
    const pbNetPrice = priceBookItem ? priceBookItem.price * (1 - priceBookItem.discount / 100) : null;
    const ticketDiscountedPrice = ticketItem?.unit_price !== null && ticketItem?.unit_price !== undefined
      ? ticketItem.unit_price * (1 - (ticketItem.discount ?? 0) / 100)
      : null;
    const priceMismatchOnPriceBook = pbNetPrice !== null && invDiscountedPrice !== null && Math.abs(invDiscountedPrice - pbNetPrice) > 0.01;
    const priceMismatchOnTicket = ticketDiscountedPrice !== null && invDiscountedPrice !== null && Math.abs(invDiscountedPrice - ticketDiscountedPrice) > 0.01;
    const priceMismatch = priceMismatchOnPriceBook || priceMismatchOnTicket;
    const totalMismatch = ticketItem !== null && invItem.line_total !== null && ticketItem.line_total !== null && Math.abs(invItem.line_total - ticketItem.line_total) > 0.01;

    const missingPriceBookItem = hasPriceBook && priceBookItem === null;
    const missingTicketItem = hasTicket && ticketItem === null;

    const hasMismatch = qtyMismatch || priceMismatch || totalMismatch || missingPriceBookItem || missingTicketItem || aiNonCompliant;

    return {
      invoiceItem: invItem,
      ticketItem,
      priceBookItem,
      hasMismatch,
      qtyMismatch,
      priceMismatch,
      priceMismatchOnPriceBook,
      priceMismatchOnTicket,
      totalMismatch,
      missingPriceBookItem,
      missingTicketItem,
    };
  });

  const toggleSelect = (id: string) => {
    const newSet = new Set(selectedItems);
    if (newSet.has(id)) {
      newSet.delete(id);
    } else {
      newSet.add(id);
    }
    setSelectedItems(newSet);
  };

  const toggleSelectAll = () => {
    if (selectedItems.size === invoiceItems.length) {
      setSelectedItems(new Set());
    } else {
      setSelectedItems(new Set(invoiceItems.map((i) => i.id)));
    }
  };

  const formatNumber = (val: number | null, decimals = 2) => {
    if (val === null || val === undefined) return '—';
    return val.toLocaleString('en-US', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
  };

  const formatDiscount = (val: number | null) => {
    if (val === null || val === undefined) return '—';
    return `${val}%`;
  };

  const formatCurrency = (val: number | null) => {
    if (val === null || val === undefined) return '—';
    return `$${val.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  const handleNotificationAction = () => {
    if (!onNotificationRequest) return;

    // If items are selected, use only selected items with mismatches; otherwise use all with mismatches
    const groupsToUse = selectedItems.size > 0
      ? comparisonGroups.filter((g) => selectedItems.has(g.invoiceItem.id) && g.hasMismatch)
      : comparisonGroups.filter((g) => g.hasMismatch);

    const messages: NonComplianceMessage[] = groupsToUse.map((group) => {
      const inv = group.invoiceItem;
      const ticket = group.ticketItem;
      const pb = group.priceBookItem;
      const invDiscountedPrice = inv.unit_price !== null
        ? inv.unit_price * (1 - (inv.discount ?? 0) / 100)
        : null;
      const pbNetPrice = pb ? pb.price * (1 - pb.discount / 100) : null;
      const ticketDiscountedPrice = ticket?.unit_price !== null && ticket?.unit_price !== undefined
        ? ticket.unit_price * (1 - (ticket.discount ?? 0) / 100)
        : null;

      const msgs: string[] = [];

      if (group.qtyMismatch && ticket) {
        msgs.push(
          `Qty Mismatch: Field Ticket Qty is ${formatNumber(ticket.quantity)} but Invoice Qty is ${formatNumber(inv.quantity)}`
        );
      }
      if (group.priceMismatchOnPriceBook) {
        msgs.push(
          `Price Mismatch: Invoice Disc'd Price is ${formatCurrency(invDiscountedPrice)} but Price Book Disc'd Price is ${formatCurrency(pbNetPrice)}`
        );
      }
      if (group.priceMismatchOnTicket) {
        msgs.push(
          `Price Mismatch: Invoice Disc'd Price is ${formatCurrency(invDiscountedPrice)} but Field Ticket Disc'd Price is ${formatCurrency(ticketDiscountedPrice)}`
        );
      }
      if (group.missingPriceBookItem) {
        msgs.push('Non-contract Item: No supporting price book item');
      }
      if (group.missingTicketItem) {
        msgs.push('Missing Ticket Item: No supporting field ticket item');
      }

      // Append AI compliance note if engine flagged this item
      const aiMatch = complianceByItemId.get(inv.id)
        ?? (inv.item_code ? complianceByCode.get(inv.item_code.toLowerCase()) : undefined)
        ?? complianceByLineNumber.get(inv.sequence)
        ?? null;
      if ((aiMatch?.status === 'FAIL' || aiMatch?.status === 'FLAG') && aiMatch.notes) {
        msgs.push(`Compliance Note: ${aiMatch.notes}`);
      }

      return {
        itemCode: inv.item_code,
        description: inv.description,
        messages: msgs,
      };
    });

    onNotificationRequest(messages);
  };

  // Apply compliance filter to groups
  const filteredGroups = complianceFilter
    ? comparisonGroups.filter((group) => {
        if (complianceFilter === 'qtyMismatch') return group.qtyMismatch;
        if (complianceFilter === 'priceMismatch') return group.priceMismatch;
        if (complianceFilter === 'unsupportedItem') return group.missingPriceBookItem;
        if (complianceFilter === 'noTicketSupport') return group.missingTicketItem;
        return true;
      })
    : comparisonGroups;

  return (
    <div className="flex-1 overflow-hidden flex flex-col">
      {complianceFilter && (
        <div className="flex items-center gap-2 mb-3">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 text-primary text-xs font-medium px-3 py-1">
            {complianceFilterLabels[complianceFilter]}
            <button
              onClick={onComplianceFilterClear}
              className="ml-0.5 rounded-full hover:bg-primary/20 p-0.5"
            >
              <X className="w-3 h-3" />
            </button>
          </span>
        </div>
      )}
      <div className="flex items-center justify-between mb-4">
        <span className="text-sm text-muted-foreground">{invoiceItems.length} items</span>
        <div className="flex items-center gap-6">
        <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer">
          <Checkbox
            checked={showOnlyNonCompliant}
            onCheckedChange={(checked) => setShowOnlyNonCompliant(checked === true)}
          />
          Show only non-compliant items
        </label>
        <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer">
          <Checkbox
            checked={showOnlyInvoice}
            onCheckedChange={(checked) => setShowOnlyInvoice(checked === true)}
          />
          Show only items from the Invoice
        </label>
        {onNotificationRequest && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm">
                Actions <ChevronDown className="w-3 h-3 ml-1" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={handleNotificationAction}>
                <Mail className="w-4 h-4 mr-2" /> Notification to Vendor...
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
        </div>
      </div>

      <div className="flex-1 overflow-auto">
        <div className="bg-card border border-border rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-muted/50">
                <th className="w-10 px-2 py-2 text-left">
                  <Checkbox
                    checked={selectedItems.size === invoiceItems.length && invoiceItems.length > 0}
                    onCheckedChange={toggleSelectAll}
                  />
                </th>
                <th className="w-8 px-1 py-2"></th>
                <th className="px-2 py-2 text-left text-xs font-medium text-muted-foreground uppercase w-24">
                  Item Code
                </th>
                <th className="px-2 py-2 text-left text-xs font-medium text-muted-foreground uppercase">
                  Item Description
                </th>
                <th className="px-2 py-2 text-left text-xs font-medium text-muted-foreground uppercase w-24">
                  Document
                </th>
                <th className="px-2 py-2 text-right text-xs font-medium text-muted-foreground uppercase w-16">
                  Qty
                </th>
                <th className="px-2 py-2 text-left text-xs font-medium text-muted-foreground uppercase w-14">
                  UoM
                </th>
                <th className="px-2 py-2 text-right text-xs font-medium text-muted-foreground uppercase w-20">
                  Price
                </th>
                <th className="px-2 py-2 text-right text-xs font-medium text-muted-foreground uppercase w-14">
                  Disc%
                </th>
                <th className="px-2 py-2 text-right text-xs font-medium text-muted-foreground uppercase w-20">
                  Disc'd Price
                </th>
                <th className="px-2 py-2 text-right text-xs font-medium text-muted-foreground uppercase w-24">
                  Total Amt
                </th>
              </tr>
            </thead>
              <tbody>
                {filteredGroups
                  .filter((group) => !showOnlyNonCompliant || group.hasMismatch)
                  .map((group, groupIndex) => {
                  const inv = group.invoiceItem;
                  const ticket = group.ticketItem;
                  const pb = group.priceBookItem;
                  const pbNetPrice = pb ? pb.price * (1 - pb.discount / 100) : null;
                  const invDiscountedPrice = inv.unit_price !== null
                    ? inv.unit_price * (1 - (inv.discount ?? 0) / 100)
                    : null;
                  const ticketDiscountedPrice = ticket?.unit_price !== null && ticket?.unit_price !== undefined
                    ? ticket.unit_price * (1 - (ticket.discount ?? 0) / 100)
                    : null;

                  // Calculate overcharge amounts for tooltip
                  const qtyOvercharge = group.qtyMismatch && ticket && inv.quantity !== null && ticket.quantity !== null && invDiscountedPrice !== null
                    ? (inv.quantity - ticket.quantity) * invDiscountedPrice
                    : null;
                  const priceOverchargeFromPb = group.priceMismatchOnPriceBook && pbNetPrice !== null && invDiscountedPrice !== null && inv.quantity !== null
                    ? (invDiscountedPrice - pbNetPrice) * inv.quantity
                    : null;
                  const priceOverchargeFromTicket = group.priceMismatchOnTicket && ticketDiscountedPrice !== null && invDiscountedPrice !== null && inv.quantity !== null
                    ? (invDiscountedPrice - ticketDiscountedPrice) * inv.quantity
                    : null;
                  const priceOvercharge = (priceOverchargeFromPb ?? 0) + (priceOverchargeFromTicket ?? 0);
                  const totalOvercharge = (qtyOvercharge ?? 0) + priceOvercharge;

                  // Match AI compliance line item — prefer deterministic invoice_item_id, fall back for older runs
                  const aiMatch = complianceByItemId.get(inv.id)
                    ?? (inv.item_code ? complianceByCode.get(inv.item_code.toLowerCase()) : undefined)
                    ?? complianceByLineNumber.get(inv.sequence)
                    ?? null;
                  const isAiExpanded = expandedAiRows.has(inv.id);

                  const hasSurfaceMismatch = group.qtyMismatch || group.priceMismatch || group.totalMismatch || group.missingPriceBookItem || group.missingTicketItem;
                  const isFlagOnly = !hasSurfaceMismatch && aiMatch?.status === 'FLAG';

                  const statusConfig: Record<string, { label: string; className: string }> = {
                    PASS: { label: 'Pass', className: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' },
                    FAIL: { label: 'Fail', className: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400' },
                    FLAG: { label: 'Flag', className: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400' },
                  };

                  return (
                    <>
                      {/* Spacer row with border between groups */}
                      {groupIndex > 0 && (
                        <tr key={`${inv.id}-spacer`} className="h-2.5 border-t border-border" />
                      )}
                      {/* Invoice Row */}
                      <tr key={inv.id} className="bg-muted/20 hover:bg-muted/40">
                        <td className="px-2 py-1.5">
                          <Checkbox
                            checked={selectedItems.has(inv.id)}
                            onCheckedChange={() => toggleSelect(inv.id)}
                          />
                        </td>
                        <td className="px-1 py-1.5">
                          <div className="flex items-center gap-1">
                          {group.hasMismatch ? (
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <AlertCircle className={`w-4 h-4 cursor-pointer ${isFlagOnly ? 'text-amber-500' : 'text-destructive'}`} />
                                </TooltipTrigger>
                                <TooltipContent side="right" className="p-0 max-w-lg tooltip-high-contrast">
                                  <table className="text-xs">
                                    <thead>
                                      <tr className="border-b bg-slate-100 dark:bg-slate-200">
                                        <th className="px-3 py-2 text-left font-medium">Type</th>
                                        <th className="px-3 py-2 text-left font-medium">Non-compliance Description</th>
                                        <th className="px-3 py-2 text-right font-medium">Overcharge</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {group.qtyMismatch && ticket && (
                                        <tr className="border-b">
                                          <td className="px-3 py-2">Qty Mismatch</td>
                                          <td className="px-3 py-2">
                                            Field Ticket Qty is {formatNumber(ticket.quantity)} but Invoice Qty is {formatNumber(inv.quantity)}
                                          </td>
                                          <td className="px-3 py-2 text-right tabular-nums">
                                            {formatCurrency(qtyOvercharge)}
                                          </td>
                                        </tr>
                                      )}
                                      {group.priceMismatchOnPriceBook && (
                                        <tr className="border-b">
                                          <td className="px-3 py-2">Price Mismatch (Price Book)</td>
                                          <td className="px-3 py-2">
                                            Invoice Disc'd Price is {formatCurrency(invDiscountedPrice)} but Price Book Disc'd Price is {formatCurrency(pbNetPrice)}
                                          </td>
                                          <td className="px-3 py-2 text-right tabular-nums">
                                            {formatCurrency(priceOverchargeFromPb)}
                                          </td>
                                        </tr>
                                      )}
                                      {group.priceMismatchOnTicket && (
                                        <tr className="border-b">
                                          <td className="px-3 py-2">Price Mismatch (Ticket)</td>
                                          <td className="px-3 py-2">
                                            Invoice Disc'd Price is {formatCurrency(invDiscountedPrice)} but Field Ticket Disc'd Price is {formatCurrency(ticketDiscountedPrice)}
                                          </td>
                                          <td className="px-3 py-2 text-right tabular-nums">
                                            {formatCurrency(priceOverchargeFromTicket)}
                                          </td>
                                        </tr>
                                      )}
                                      {group.missingPriceBookItem && (
                                        <tr className="border-b">
                                          <td className="px-3 py-2">Non-contract Item</td>
                                          <td className="px-3 py-2">
                                            <span>The invoice item does not have a supporting price book item</span>
                                            {priceBookItems.length > 0 && (
                                              <Button
                                                variant="ghost"
                                                size="sm"
                                                className="ml-2 h-6 px-2 text-xs"
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  setMatchDialogItem(inv);
                                                }}
                                              >
                                                <Sparkles className="w-3 h-3 mr-1" /> Check Match
                                              </Button>
                                            )}
                                          </td>
                                          <td className="px-3 py-2 text-right tabular-nums">
                                            —
                                          </td>
                                        </tr>
                                      )}
                                      {group.missingTicketItem && (
                                        <tr className="border-b">
                                          <td className="px-3 py-2">Missing Ticket Item</td>
                                          <td className="px-3 py-2">
                                            The invoice item does not have a supporting field ticket item
                                          </td>
                                          <td className="px-3 py-2 text-right tabular-nums">
                                            —
                                          </td>
                                        </tr>
                                      )}
                                    </tbody>
                                    {(group.qtyMismatch || group.priceMismatch) && (qtyOvercharge !== null || priceOvercharge !== null) && (
                                      <tfoot>
                                        <tr className="bg-red-50 dark:bg-red-100 font-semibold">
                                          <td className="px-3 py-2" colSpan={2}>Total</td>
                                          <td className="px-3 py-2 text-right tabular-nums text-red-600">
                                            {formatCurrency(totalOvercharge)}
                                          </td>
                                        </tr>
                                      </tfoot>
                                    )}
                                  </table>
                                <div className="flex flex-wrap gap-1.5 px-3 py-2 border-t">
                                  {group.qtyMismatch && (
                                    <span className="text-xs bg-destructive/10 text-destructive px-2 py-0.5 rounded">
                                      Qty mismatch
                                    </span>
                                  )}
                                  {group.priceMismatchOnPriceBook && (
                                    <span className="text-xs bg-destructive/10 text-destructive px-2 py-0.5 rounded">
                                      Price mismatch (Price Book)
                                    </span>
                                  )}
                                  {group.priceMismatchOnTicket && (
                                    <span className="text-xs bg-destructive/10 text-destructive px-2 py-0.5 rounded">
                                      Price mismatch (Ticket)
                                    </span>
                                  )}
                                  {group.totalMismatch && (
                                    <span className="text-xs bg-destructive/10 text-destructive px-2 py-0.5 rounded">
                                      Total mismatch
                                    </span>
                                  )}
                                  {group.missingPriceBookItem && (
                                    <span className="text-xs bg-amber-500/10 text-amber-600 px-2 py-0.5 rounded">
                                      No price book item
                                    </span>
                                  )}
                                  {group.missingTicketItem && (
                                    <span className="text-xs bg-amber-500/10 text-amber-600 px-2 py-0.5 rounded">
                                      No ticket item
                                    </span>
                                  )}
                                </div>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          ) : (
                            <CheckCircle2 className="w-4 h-4 text-success" />
                          )}
                          {aiMatch && (
                            <button
                              onClick={(e) => { e.stopPropagation(); toggleAiRow(inv.id); }}
                              className="p-0.5 rounded hover:bg-muted/50"
                              title="Toggle AI compliance detail"
                            >
                              {isAiExpanded
                                ? <ChevronDown className="w-3.5 h-3.5 text-muted-foreground" />
                                : <ChevronRight className="w-3.5 h-3.5 text-muted-foreground" />
                              }
                            </button>
                          )}
                          {showComplianceBadges && aiMatch && (
                            <Badge className={`text-[10px] px-1.5 py-0 leading-tight ${statusConfig[aiMatch.status]?.className ?? ''}`}>
                              {statusConfig[aiMatch.status]?.label ?? aiMatch.status}
                            </Badge>
                          )}
                          </div>
                        </td>
                        <td className="px-2 py-1.5 font-mono text-sm text-muted-foreground">
                          {inv.item_code || '—'}
                        </td>
                        <td className={cn("px-2 py-1.5 font-medium", group.hasMismatch && (isFlagOnly ? "text-amber-500" : "text-destructive"))}>
                          {inv.description}
                        </td>
                        <td className="px-2 py-1.5 text-muted-foreground">Invoice</td>
                        <td className={cn(
                          "px-2 py-1.5 text-right tabular-nums",
                          group.qtyMismatch && "text-destructive font-medium"
                        )}>
                          {formatNumber(inv.quantity)}
                        </td>
                        <td className="px-2 py-1.5 whitespace-nowrap">{inv.uom || '—'}</td>
                        <td className="px-2 py-1.5 text-right tabular-nums">
                          {formatNumber(inv.unit_price)}
                        </td>
                        <td className="px-2 py-1.5 text-right tabular-nums">{formatDiscount(inv.discount)}</td>
                        <td className={cn(
                          "px-2 py-1.5 text-right tabular-nums",
                          group.priceMismatch && "text-destructive font-medium"
                        )}>
                          {formatNumber(inv.unit_price !== null ? inv.unit_price * (1 - (inv.discount ?? 0) / 100) : null)}
                        </td>
                        <td className={cn(
                          "px-2 py-1.5 text-right tabular-nums font-medium",
                          group.totalMismatch && "text-destructive"
                        )}>
                          {formatNumber(inv.line_total)}
                        </td>
                      </tr>

                      {/* AI Compliance Detail Row */}
                      {aiMatch && isAiExpanded && (
                        <tr key={`${inv.id}-ai-compliance`} className="bg-muted/10">
                          <td className="px-2 py-1"></td>
                          <td className="px-1 py-1"></td>
                          <td colSpan={9} className="px-3 py-3">
                            <ComplianceLineItemDetail item={aiMatch} />
                          </td>
                        </tr>
                      )}

                      {/* Ticket Row */}
                      {!showOnlyInvoice && ticket && (
                        <tr key={`${inv.id}-ticket`} className="bg-muted/20 hover:bg-muted/40">
                          <td className="px-2 py-1"></td>
                          <td className="px-1 py-1"></td>
                          <td className="px-2 py-1 font-mono text-sm text-muted-foreground">
                            {ticket.item_code || '—'}
                          </td>
                          <td className="px-2 py-1 text-muted-foreground">
                            {ticket.description}
                          </td>
                          <td className="px-2 py-1 text-muted-foreground">Field Ticket</td>
                          <td className={cn(
                            "px-2 py-1 text-right tabular-nums text-muted-foreground",
                            group.qtyMismatch && "text-destructive"
                          )}>
                            {formatNumber(ticket.quantity)}
                          </td>
                          <td className="px-2 py-1 text-muted-foreground whitespace-nowrap">{ticket.uom || '—'}</td>
                          <td className="px-2 py-1 text-right tabular-nums text-muted-foreground">
                            {formatNumber(ticket.unit_price)}
                          </td>
                          <td className="px-2 py-1 text-right tabular-nums text-muted-foreground">
                            {formatDiscount(ticket.discount)}
                          </td>
                          <td className="px-2 py-1 text-right tabular-nums text-muted-foreground">
                            {formatNumber(ticket.unit_price !== null ? ticket.unit_price * (1 - (ticket.discount ?? 0) / 100) : null)}
                          </td>
                          <td className={cn(
                            "px-2 py-1 text-right tabular-nums text-muted-foreground",
                            group.totalMismatch && "text-destructive"
                          )}>
                            {formatNumber(ticket.line_total)}
                          </td>
                        </tr>
                      )}

                      {/* Price Book Row */}
                      {!showOnlyInvoice && pb && (
                        <tr key={`${inv.id}-pb`} className="bg-muted/20 hover:bg-muted/40">
                          <td className="px-2 py-1"></td>
                          <td className="px-1 py-1"></td>
                          <td className="px-2 py-1 font-mono text-sm text-muted-foreground">
                            {pb.itemcode || '—'}
                          </td>
                          <td className="px-2 py-1 text-muted-foreground">
                            {pb.description}
                          </td>
                          <td className="px-2 py-1 text-muted-foreground">Price Book</td>
                          <td className="px-2 py-1 text-right tabular-nums text-muted-foreground">—</td>
                          <td className="px-2 py-1 text-muted-foreground whitespace-nowrap">{pb.unit || '—'}</td>
                          <td className="px-2 py-1 text-right tabular-nums text-muted-foreground">
                            {formatNumber(pb.price)}
                          </td>
                          <td className="px-2 py-1 text-right tabular-nums text-muted-foreground">
                            {formatDiscount(pb.discount)}
                          </td>
                          <td className={cn(
                            "px-2 py-1 text-right tabular-nums text-muted-foreground",
                            group.priceMismatch && "text-destructive"
                          )}>
                            {formatNumber(pbNetPrice)}
                          </td>
                          <td className="px-2 py-1 text-right tabular-nums text-muted-foreground">—</td>
                        </tr>
                      )}

                      {/* Missing matches notices */}
                      {!showOnlyInvoice && !ticket && !pb && (
                        <tr key={`${inv.id}-none`} className="bg-muted/20">
                          <td className="px-2 py-1"></td>
                          <td className="px-1 py-1"></td>
                          <td colSpan={9} className="px-2 py-1 text-muted-foreground text-xs italic">
                            No matching ticket or price book items found
                          </td>
                        </tr>
                      )}
                      {!showOnlyInvoice && ticket && !pb && hasPriceBook && (
                        <tr key={`${inv.id}-no-pb`} className="bg-muted/20">
                          <td className="px-2 py-1"></td>
                          <td className="px-1 py-1"></td>
                          <td colSpan={9} className="px-2 py-1 text-muted-foreground text-xs italic">
                            No price book item found
                          </td>
                        </tr>
                      )}
                      {!showOnlyInvoice && !ticket && pb && hasTicket && (
                        <tr key={`${inv.id}-no-ticket`} className="bg-muted/20">
                          <td className="px-2 py-1"></td>
                          <td className="px-1 py-1"></td>
                          <td colSpan={9} className="px-2 py-1 text-muted-foreground text-xs italic">
                            No field ticket item found
                          </td>
                        </tr>
                      )}
                    </>
                  );
                })}
              </tbody>
            </table>

          {invoiceItems.length === 0 && (
            <div className="text-center py-8 text-muted-foreground">
              No invoice items found.
            </div>
          )}
        </div>
      </div>

      <PriceBookMatchDialog
        open={matchDialogItem !== null}
        onOpenChange={(open) => { if (!open) setMatchDialogItem(null); }}
        invoiceItem={matchDialogItem}
        priceBookItems={priceBookItems}
      />
    </div>
  );
}
